import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';

import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';

import {
  ArrowNavigation,
  MultiSelect,
  VirtualizedAutocomplete
} from 'components/shared';
import { optionPadding } from 'components/shared/Autocomplete/MuiAutocompleteOption';
import { FieldState } from 'components/flex-portal/FlexMatch/components/form/PreferencesSection.types';
import { defaultListWidth } from './Autocomplete/VirtualizedAutocomplete.consts';

const EmployeeSelectorStyles = styled.div`
  display: flex;
  width: 100%;

  .controls {
    margin-left: 5px;
  }

  .MuiAutocomplete-root {
    width: 100%;
  }
`;

export type Employee = {
  id: number;
  employeeId: number;
  employeeName?: string;
  nr: number;
  employeeNr?: number;
  label: string;
};

export type EmployeeSelectorHandle = {
  uncheckEmployee: (
    employee: Employee,
    shouldTriggerChangeEvent?: boolean
  ) => void;
  checkEmployee: (
    employee: Employee,
    shouldTriggerChangeEvent?: boolean
  ) => void;
  setSelectedEmployees: (employees: Employee[]) => void;
};

type Props = {
  options: Employee[];
  showNavigation?: boolean;
  disablePortal?: true;
  selectedValue?: Employee;
  disabled?: boolean;
  variant?: 'standard' | 'outlined';
  fieldState?: FieldState;
  fullWidth?: boolean;
} & (
  | {
      isMulti: true;
      onSelected: (employees: Employee[]) => void;
    }
  | {
      isMulti?: never;
      onSelected: (employee: Employee) => void;
    }
);

export default forwardRef(function EmployeeSelector(props: Props, ref) {
  const {
    options = [],
    showNavigation,
    disablePortal,
    isMulti,
    selectedValue,
    onSelected,
    disabled,
    variant = 'standard',
    fieldState,
    fullWidth = false
  } = props;
  const { t } = useTranslation('common');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null
  );
  const [selectedEmployees, setSelectedEmployees] = useState<Employee[]>([]);

  const onArrowNavigate = (direction, currentIndex) => {
    let nextEmployee: Employee;
    if (direction === 'prev') {
      nextEmployee = options[currentIndex - 1];
    } else {
      nextEmployee = options[selectedEmployee ? currentIndex + 1 : 0];
    }

    if (isMulti === true) {
      setSelectedEmployees([nextEmployee]);
      props.onSelected([nextEmployee]);
    } else {
      setSelectedEmployee(nextEmployee);
      props.onSelected(nextEmployee);
    }
  };

  const renderInput = (params) => {
    const { value } = params.inputProps;
    if (/\d+\s-\s/.test(value)) {
      params.InputProps.startAdornment = (
        <InputAdornment position="start">
          <small>
            <i>{value.match(/\d+/)[0]}</i>
          </small>
          &nbsp; -
        </InputAdornment>
      );
      params.inputProps.value = value.replace(/\d+\s-\s/, '');
    }
    return (
      <TextField
        {...params}
        variant={variant}
        size="small"
        label={t('employeeName')}
        fullWidth={fullWidth}
        sx={{
          width: fullWidth ? '100%' : defaultListWidth
        }}
        {...(fieldState
          ? { error: !!fieldState.error, helperText: fieldState.error?.message }
          : {})}
      />
    );
  };

  useEffect(() => {
    if (isMulti) {
      if (selectedEmployees.length) {
        const filteredEmployees = selectedEmployees.filter((selectedEmployee) =>
          options.find((o) => o.id === selectedEmployee.id)
        );
        if (filteredEmployees.length !== selectedEmployees.length) {
          setSelectedEmployees(filteredEmployees);
          props.onSelected(filteredEmployees);
        }
      }
    } else if (selectedValue) {
      setSelectedEmployee(selectedValue);
      props.onSelected(selectedValue);
    }
  }, [options, isMulti, selectedEmployees, selectedValue, onSelected]);

  useImperativeHandle(ref, () => ({
    uncheckEmployee(employee, shouldTriggerChangeEvent = true) {
      const newSelection = selectedEmployees.filter(
        ({ employeeId }) => employeeId !== employee.employeeId
      );
      setSelectedEmployees(newSelection);
      if (shouldTriggerChangeEvent && isMulti) {
        props.onSelected(newSelection);
      }
    },
    checkEmployee(employee, shouldTriggerChangeEvent = false) {
      const newSelection = [...selectedEmployees, employee];
      setSelectedEmployees(newSelection);
      if (shouldTriggerChangeEvent && isMulti) {
        props.onSelected(newSelection);
      }
    },
    setSelectedEmployees(employees) {
      setSelectedEmployees(employees);
    }
  }));

  return (
    <EmployeeSelectorStyles className="employee-selector">
      {props.isMulti ? (
        <MultiSelect
          optionLabelProp="label"
          renderInput={renderInput}
          options={options}
          selectedOptions={selectedEmployees}
          onChange={(options) => {
            setSelectedEmployees(options);
            props.onSelected(options);
          }}
          disablePortal={disablePortal}
          disabled={disabled}
        />
      ) : (
        <VirtualizedAutocomplete
          value={selectedEmployee}
          options={options}
          optionLabelProp="label"
          getOptionLabel={(o) => {
            if (typeof o === 'string') {
              return o;
            }

            return o?.label || '';
          }}
          renderInput={renderInput}
          onChange={(e, value: any) => {
            setSelectedEmployee(value || null);
            onSelected(value || null);
          }}
          autoHighlight
          extraWidth={optionPadding}
          disablePortal={disablePortal}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          disabled={disabled}
        />
      )}
      {showNavigation && !isMulti && (
        <ArrowNavigation
          selectedOption={selectedEmployee}
          options={options}
          showCnt
          onNavigate={onArrowNavigate}
        />
      )}
    </EmployeeSelectorStyles>
  );
});
